import { ChevronUpIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { ValidatedForm } from 'remix-validated-form'
import { z } from 'zod'
import FormInput from 'app/components/form/FormInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import SimpleTable from 'app/components/table/SimpleTable'
import type { UseOpsSearchToolReturn } from './useOpsSearchTool'

const elasticSearchValidator = withZod(z.object({ index: z.string(), query: z.string() })) as any

export function JsonCell({ value }: { value: string }) {
  const [isExpanded, setIsExpanded] = useState(true)
  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <>
      <div className="flex flex-row">
        <ChevronUpIcon
          className={`h-4 w-4 shrink-0 ${isExpanded ? `rotate-180` : `rotate-90`}`}
          onClick={toggleExpand}
        />
        {isExpanded ? (
          <pre className=" pl-8 text-xs">
            <code>{value}</code>
          </pre>
        ) : (
          <div className="whitespace-normal pl-8">{typeof value === `undefined` ? `-` : value}</div>
        )}
      </div>
    </>
  )
}

const defaultColumns: ColumnDef<any, any>[] = [
  {
    accessorFn: (row) => JSON.stringify(row, null, 2),
    id: `results`,
    header: `Results`,
    // cell: (props) => <JsonCell value={props.getValue()} />,
    meta: {
      defaultVisible: true,
      ControlledComponent: (row, col, index) => <JsonCell value={JSON.stringify(row, null, 2)} />,
    },
  },
]

export type OpsSearchToolComponentProps = UseOpsSearchToolReturn

export function OpsSearchToolComponent(props: OpsSearchToolComponentProps) {
  const { loaderData, formData, handleChange, submitHandler } = props

  const esIndexOptions = [
    { label: `Activity`, value: `activity` },
    { label: `Appointment`, value: `appointment` },
    { label: `Call`, value: `call` },
    { label: `Call Data`, value: `call_data` },
    { label: `Contact`, value: `contact` },
    { label: `Location`, value: `location` },
    { label: `Patient`, value: `patient` },
    { label: `Transaction`, value: `transaction` },
    { label: `User`, value: `user` },
    { label: `Workspace`, value: `workspace` },
  ]

  return (
    <PageScroll variant="table">
      <div className="flex h-full flex-col gap-4 pt-8">
        <PageHeader title="Search Tool" totalItems={`${loaderData?.total ? loaderData.total : 0} items`} />
        <p>
          See here for syntax help:{` `}
          <a
            className="btn btn-link inline"
            target="_blank"
            rel="noreferrer"
            href="https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-query-string-query.html#query-string-query-notes"
          >
            ElasticSearch
          </a>
        </p>
        <ValidatedForm
          validator={elasticSearchValidator}
          onSubmit={submitHandler}
          className="mt-6 grid w-full max-w-3xl flex-row flex-nowrap justify-between gap-x-4 gap-y-6 sm:grid-cols-6"
        >
          <FormInput
            name="query"
            label="Query"
            cols={3}
            labelClassName="text-ui-sm"
            className="flex-auto rounded-lg border border-default bg-surface px-4 py-3 text-ui-sm text-subtle shadow-elevation-1"
            value={formData.query}
            onChange={handleChange}
          />
          <FormSelect
            name="index"
            label="Index"
            cols={2}
            labelClassName="text-ui-sm"
            options={esIndexOptions}
            value={formData.index}
            onChange={handleChange}
          />
          <button type="submit" className="btn btn-primary btn-md self-end">
            Search
          </button>
        </ValidatedForm>
        {loaderData?.error && (
          <>
            <h5 className="text-red-600">Error Encountered While Searching</h5>
            <pre className="overflow-auto whitespace-pre-wrap text-xs text-red-600">
              <code>{JSON.stringify(loaderData.error, null, 2)}</code>
            </pre>
          </>
        )}
        {loaderData && !loaderData.error ? (
          loaderData.total > 0 ? (
            <SimpleTable
              data={loaderData.hits}
              columns={defaultColumns}
              showColumnOrdering
              enableExport
              hideFooter
              edgePadded
            />
          ) : (
            <h5>No Results Found</h5>
          )
        ) : null}
      </div>
    </PageScroll>
  )
}
